import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import "../../../auth-modern.css";

const PrivacyPolicy = () => (
  <>
    <Helmet>
      <title>Privacy Policy - Planit</title>
    </Helmet>

    <div className="auth-modern-wrapper" style={{ alignItems: "center", justifyContent: "flex-start", padding: "40px 20px 60px" }}>
      <div style={{ width: "100%", maxWidth: "860px" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <Link to="/">
            <img src="/assets/images/logo-dark.png" alt="Planit" height={32} />
          </Link>
        </div>

        <div style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "48px 56px",
          boxShadow: "0 2px 20px rgba(0,0,0,0.07)",
          fontFamily: "'Inter', 'Roboto', sans-serif",
          color: "#1D1D1D",
          lineHeight: "1.75",
          fontSize: "15px",
        }}>
          <h1 style={{ fontSize: "26px", fontWeight: 600, marginBottom: "4px" }}>Privacy Policy</h1>
          <p style={{ color: "#74788d", marginBottom: "36px", fontSize: "14px" }}>Effective date: 12 October 2026</p>

          <Section title="1. About this policy">
            <p>This policy explains how Websouls collects, uses and protects personal information in Planit, our HR and project management platform. It applies to the Planit mobile apps for iOS and Android and the Planit web application (together, the "Service").</p>
            <p>Planit is a product of Websouls. In this policy, "Websouls", "we", "us" and "our" mean Websouls, with its registered office at:</p>
            <address style={{ fontStyle: "normal", paddingLeft: "16px", borderLeft: "3px solid #00DFAB", marginBottom: "12px" }}>
              Office # 22, 2nd Office Floor, IT Tower, Behind Hafeez Center, 73-E-1 Hali Road, Gulberg III, Lahore, Pakistan
            </address>
            <p>Planit is a business tool. Organisations ("Customers") subscribe to Planit and give their employees and team members ("Users") access to it. There is no public sign-up: every User account is created by the User's organisation.</p>
          </Section>

          <Section title="2. Who is responsible for your information">
            <p>Your organisation decides what information about you goes into Planit and how it is used. Websouls processes that information on your organisation's behalf, under our contract with them.</p>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Information</Th>
                  <Th>Who is responsible</Th>
                  <Th>Websouls' role</Th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Td>Employee and work data your organisation or you add to Planit (HR records, attendance, leave, tasks, projects, notes, meetings, messages, files)</Td>
                  <Td>Your organisation</Td>
                  <Td>Processor: we handle it only on your organisation's instructions</Td>
                </tr>
                <tr>
                  <Td>Customer account and contact details (for example, the admin who manages the subscription)</Td>
                  <Td>Websouls</Td>
                  <Td>Controller</Td>
                </tr>
                <tr>
                  <Td>Technical data about how the app runs (device type, app version, crash reports, security logs)</Td>
                  <Td>Websouls</Td>
                  <Td>Controller</Td>
                </tr>
                <tr>
                  <Td>Support requests you send directly to Websouls</Td>
                  <Td>Websouls</Td>
                  <Td>Controller</Td>
                </tr>
              </tbody>
            </table>
            <p>If you have questions about how your organisation uses your HR or work information, please contact your organisation's HR team or Planit administrator first. We will also help where we can (see section 13).</p>
          </Section>

          <Section title="3. Information we collect">
            <p><strong>Information provided by your organisation.</strong> Your organisation's administrators create your account and may add:</p>
            <ul>
              <li>Identity and contact details: name, work email, phone number, profile photo</li>
              <li>Employment details: employee ID, job title, department, manager, joining date, employment status</li>
              <li>HR records: attendance, leave, working hours and schedules</li>
              <li>Personal and financial details: national ID (CNIC) or passport number, salary, bank details, emergency contacts, and uploaded employee documents</li>
            </ul>
            <p><strong>Information you add while using Planit.</strong> Tasks, projects, comments, timesheets, notes, meeting agendas and minutes, chat messages, and files you upload.</p>
            <p><strong>Information collected automatically.</strong></p>
            <ul>
              <li>Device and app information: device model, operating system, app version, language</li>
              <li>Log and security data: IP address, login times, actions taken in the app (for security and troubleshooting)</li>
              <li>Crash and performance reports</li>
              <li>Push notification token, so we can send you notifications</li>
            </ul>
            <p><strong>Device permissions.</strong> The app asks for permission before accessing any of the following. You can turn each one off in your phone's settings; some features will then stop working.</p>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Permission</Th>
                  <Th>What it is used for</Th>
                  <Th>Required?</Th>
                </tr>
              </thead>
              <tbody>
                <tr><Td>Notifications</Td><Td>Alerts about tasks, approvals, meetings and messages</Td><Td>Optional</Td></tr>
                <tr><Td>Camera</Td><Td>Taking photos to attach in chat</Td><Td>Optional</Td></tr>
                <tr><Td>Photos and files</Td><Td>Attaching files and choosing a profile photo</Td><Td>Optional</Td></tr>
                <tr><Td>Microphone</Td><Td>Recording voice notes in chat</Td><Td>Optional</Td></tr>
              </tbody>
            </table>
            <p>Planit does not access your location or your contacts.</p>
          </Section>

          <Section title="4. How we use information">
            <p>We use information only to run and protect the Service. We do not use it for advertising, and we do not sell it.</p>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <Th>Purpose</Th>
                  <Th>Legal basis</Th>
                </tr>
              </thead>
              <tbody>
                <tr><Td>Providing Planit features your organisation has subscribed to</Td><Td>Your organisation's instructions (we act as processor); for our own data, performance of our contract with your organisation</Td></tr>
                <tr><Td>Signing you in, keeping accounts secure, preventing fraud and misuse</Td><Td>Legitimate interests (keeping the Service secure)</Td></tr>
                <tr><Td>Sending notifications about your work in Planit</Td><Td>Your organisation's instructions; you can turn notifications off</Td></tr>
                <tr><Td>Fixing bugs and improving reliability, using crash and performance data</Td><Td>Legitimate interests (keeping the Service working)</Td></tr>
                <tr><Td>Responding to support requests</Td><Td>Legitimate interests; performance of contract</Td></tr>
                <tr><Td>Meeting legal, tax and regulatory obligations</Td><Td>Legal obligation</Td></tr>
              </tbody>
            </table>
            <p>Your organisation is responsible for having a lawful basis for the HR and work information it puts into Planit, and for telling its employees how that information is used.</p>
          </Section>

          <Section title="5. How we share information">
            <p>We share information only in these cases:</p>
            <ul>
              <li><strong>Within your organisation.</strong> Your organisation's administrators, managers and colleagues can see information according to the roles and permissions your organisation sets in Planit.</li>
              <li><strong>Push notification services.</strong> To deliver notifications, we use Google Firebase Cloud Messaging (Android) and Apple Push Notification service (iOS). These services receive a device token and the notification content, and process them only to deliver the notification.</li>
              <li><strong>Legal reasons.</strong> When required by law, court order or a lawful request from authorities, or to protect the rights, safety and security of our Users, Customers or Websouls.</li>
              <li><strong>Business changes.</strong> If Websouls is involved in a merger, acquisition or sale of assets, subject to this policy continuing to apply.</li>
            </ul>
            <p>Apart from push notification delivery, we do not use third-party services to store or process your information. We do not sell personal information. We do not show ads in Planit. We do not track you across other companies' apps or websites.</p>
          </Section>

          <Section title="6. International transfers">
            <p>Planit is operated by Websouls from Pakistan. Websouls staff in Pakistan may access information to provide support and keep the Service running.</p>
            <p>Planit is used by organisations in more than one country, so information may be transferred to and accessed from Pakistan. Where the law of your country requires it, we put appropriate safeguards in place to protect information transferred across borders.</p>
          </Section>

          <Section title="7. How long we keep information">
            <p>We keep information for as long as your organisation's Planit subscription is active.</p>
            <ul>
              <li><strong>When an organisation cancels.</strong> All of that organisation's data is deleted from our live systems immediately on cancellation. Copies in backups are permanently removed within 30 days as backups are replaced. Organisations should export anything they want to keep before cancelling.</li>
              <li><strong>When a User leaves an organisation.</strong> Your organisation's administrator decides whether to deactivate or delete your account and records, in line with its own retention rules.</li>
              <li><strong>Records we must keep by law.</strong> Billing and invoice records for Customers are kept for as long as tax and accounting laws require. These do not include employees' HR data.</li>
            </ul>
          </Section>

          <Section title="8. Deleting your account and data">
            <p>Planit accounts are created and managed by your organisation, so deletion is handled through it.</p>
            <ol>
              <li><strong>Ask your organisation.</strong> Contact your HR team or Planit administrator. They can deactivate or delete your account and data in Planit.</li>
              <li><strong>Or ask us.</strong> Email <a href="mailto:support@websouls.com" style={linkStyle}>support@websouls.com</a> from the email address linked to your Planit account, with the name of your organisation. We will pass your request to your organisation and act on its instructions, and we will reply to you within 30 days.</li>
            </ol>
            <p>Some records may need to be kept by your organisation to meet employment, tax or other legal obligations. Your organisation will tell you if that applies.</p>
          </Section>

          <Section title="9. Security">
            <p>We protect information with technical and organisational measures suited to HR data, including:</p>
            <ul>
              <li>Encrypted connections (HTTPS/TLS) between the app and our servers</li>
              <li>Passwords stored in encrypted form</li>
              <li>Role-based access, so people see only what their organisation allows</li>
              <li>Restricted staff access to production systems, on a need-to-know basis</li>
            </ul>
            <p>No system is completely secure. If a breach affects your personal information, we will inform your organisation, and where the law requires, the relevant authority and you, without undue delay.</p>
          </Section>

          <Section title="10. Your rights">
            <p>Depending on where you are, you may have the right to:</p>
            <ul>
              <li>Get a copy of your personal information</li>
              <li>Have inaccurate information corrected</li>
              <li>Have your information deleted</li>
              <li>Restrict or object to certain uses of your information</li>
              <li>Receive your information in a portable format</li>
              <li>Withdraw consent, where we rely on consent</li>
            </ul>
            <p><strong>How to use these rights.</strong> For HR and work information, contact your organisation first, because it controls that data; we will support it in responding. For information Websouls controls, email us (section 13). We respond within one month and may need to confirm your identity.</p>
            <p><strong>Complaints.</strong> If you are unhappy with how your information is handled, please contact us first so we can try to resolve it. You may also have the right to complain to the data protection authority in your country.</p>
            <p>We handle personal information in line with the data protection laws that apply where our Customers and Users are based, including the Prevention of Electronic Crimes Act 2016 in Pakistan.</p>
          </Section>

          <Section title="11. Children">
            <p>Planit is a workplace tool for adults. It is not intended for anyone under 18, and we do not knowingly collect information from children.</p>
          </Section>

          <Section title="12. Changes to this policy">
            <p>We may update this policy when Planit or the law changes. We will post the new version on this page with a new effective date. If the changes are significant, we will notify Customers' administrators and, where appropriate, show a notice in the app.</p>
          </Section>

          <Section title="13. Contact us">
            <p>For privacy questions, requests or complaints:</p>
            <ul>
              <li>Email: <a href="mailto:support@websouls.com" style={linkStyle}>support@websouls.com</a></li>
              <li>Address: Websouls, Office # 22, 2nd Office Floor, IT Tower, Behind Hafeez Center, 73-E-1 Hali Road, Gulberg III, Lahore, Pakistan</li>
            </ul>
          </Section>

          <p style={{ marginTop: "48px", paddingTop: "24px", borderTop: "1px solid #eee", color: "#74788d", fontSize: "13px", textAlign: "center" }}>
            © {new Date().getFullYear()} Planit. Developed by <a href="https://websouls.com/" target="_blank" rel="noopener noreferrer" style={linkStyle}>WebSouls</a>
            &nbsp;·&nbsp;
            <Link to="/terms" style={linkStyle}>Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  </>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: "32px" }}>
    <h2 style={{ fontSize: "17px", fontWeight: 600, color: "#1D1D1D", marginBottom: "12px", paddingBottom: "8px", borderBottom: "2px solid #00DFAB" }}>{title}</h2>
    {children}
  </div>
);

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  marginBottom: "16px",
  fontSize: "14px",
};

const Th = ({ children }) => (
  <th style={{ background: "#f5fdf9", border: "1px solid #e0f5ef", padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#1D1D1D" }}>{children}</th>
);

const Td = ({ children }) => (
  <td style={{ border: "1px solid #e8e8e8", padding: "10px 14px", verticalAlign: "top" }}>{children}</td>
);

const linkStyle = { color: "#00DFAB", textDecoration: "none" };

export default PrivacyPolicy;
